import React, { useCallback, useMemo, useState } from 'react'
import { X } from 'react-feather'
import { FormattedMessage, useIntl } from 'react-intl'
import classnames from 'classnames'
import PerfectScrollbar from 'react-perfect-scrollbar'
import Select from 'react-select'

import _ from 'lodash'
import useOutsideClick from '~/hooks/useOutsideClick'
import { Hide, Show } from '~/components/commons'
import { FilterPanelContainer, FilterSection } from '../styles'
import Checkbox from '~/components/Checkbox'
import { NOTIFICATION_TYPES } from '~/constants/notifications'
import { useDispatch, useSelector } from 'react-redux'
import { switchProject } from '~/store/modules/user/actions'

function SideMenu({ children, isSideMenu = false, visible = false, onClose }) {
  const containerRef = useOutsideClick(event => {
    if (isSideMenu && visible) {
      event.stopPropagation()
      onClose()
    }
  })

  return (
    <FilterPanelContainer
      className={classnames({
        customizer: isSideMenu,
        open: visible,
      })}
      style={{ width: isSideMenu && 260 }}
      ref={containerRef}
    >
      <Show condition={isSideMenu}>
        <div className="header d-flex justify-content-between px-2 pt-2">
          <div className="close-icon cursor-pointer" onClick={onClose}>
            <X size={24} />
          </div>

          <span className="h4 m-0">
            <FormattedMessage id="Filters" />
          </span>
        </div>
      </Show>

      <PerfectScrollbar
        options={{
          wheelPropagation: true,
        }}
        className="customizer-content"
      >
        {children}
      </PerfectScrollbar>
    </FilterPanelContainer>
  )
}

function NotificationsFilterPanel({
  isVisibleSideMenu,
  setIsVisibleSideMenu,
  isMobileMode,
  queryParams,
  setQueryParams,
}) {
  const intl = useIntl()
  const [selectedProject, setSelectedProject] = useState()
  const projects = useSelector(state => state.user?.profile?.projects)
  const dispatchRedux = useDispatch()

  const options = useMemo(() => {
    if (!projects) return []
    const options = [
      { value: 'all', label: <FormattedMessage id="All projects" /> },
      ...projects.map(project => ({
        value: project.gmbAccountId,
        label: project.name.replace(/^\.INTERNAL - /, ''),
        id: project.id,
      })),
    ]
    return options
  }, [projects])

  const allNotificationsTypes = Object.keys(NOTIFICATION_TYPES)

  const handleFilterPropertyChange = useCallback(
    (filter, value) => {
      setQueryParams({ ...queryParams, [filter]: value })
    },
    [queryParams, setQueryParams]
  )

  const handleNotificationsChange = key => {
    if (queryParams.types[0] === 'all') {
      return handleFilterPropertyChange(
        'types',
        allNotificationsTypes.filter(v => v !== key)
      )
    }

    const values = _.castArray(queryParams.types)
    const index = values?.findIndex(v => v === key)
    if (index !== -1) {
      values.splice(index, 1)
      handleFilterPropertyChange(
        'types',
        values.length ? values : allNotificationsTypes
      )
    } else {
      values.push(key)
      handleFilterPropertyChange(
        'types',
        values.length === allNotificationsTypes.length ? ['all'] : values
      )
    }
  }

  const handleProjectChange = option => {
    if (option) {
      setSelectedProject(option)
      if (option.value !== 'all') dispatchRedux(switchProject(option.id))
      handleFilterPropertyChange('projects', option.value)
    } else {
      setSelectedProject(options[0])
      handleFilterPropertyChange('projects', options[0].value)
    }
  }

  return (
    <SideMenu
      isSideMenu={isMobileMode}
      visible={isVisibleSideMenu && isMobileMode}
      onClose={() => setIsVisibleSideMenu(false)}
    >
      <Hide condition={isMobileMode}>
        <FilterSection>
          <Select
            options={options}
            isClearable={selectedProject?.value !== options?.[0].value}
            placeholder={intl.formatMessage({ id: 'Select one project' })}
            onChange={option => handleProjectChange(option)}
            value={selectedProject}
          />
        </FilterSection>
        <FilterSection style={{ borderBottom: 'none' }}>
          <label
            style={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#000',
              marginBottom: '20px',
            }}
          >
            <b>
              <FormattedMessage id="Types of notifications" />
            </b>
          </label>
          {Object.entries(NOTIFICATION_TYPES).map(([key, value]) => (
            <Checkbox
              color="primary"
              className="mb-50"
              label={value}
              size="md"
              key={key}
              checked={
                queryParams.types.includes(key) ||
                queryParams.types[0] === 'all'
              }
              onChange={() => handleNotificationsChange(key)}
            />
          ))}
        </FilterSection>
      </Hide>
    </SideMenu>
  )
}

export default NotificationsFilterPanel
